import { useMemo } from 'react';
import { useInteraction } from '@/common/hooks/useInteraction';
import type { LeaseRepresentation } from '@/common/types/interaction';
import { GQLInteractionType } from '@/generated/graph-api';
import { useOfferExperience } from '@/views/Interactions/hooks/useOfferExperience';
import { useAppFlags } from '$launch-darkly/app-flags';
import { useInteractionFlags } from '$launch-darkly/useInteractionFlags';
import { getLocalCoverSheetConfig } from '../configurations/getLocalConfig';
import { COMPLIANCE_UPSTREAM_FIELD_NAMES } from '../configurations/shared/cards/complianceUpstreamFields';
import type { Card, CoverSheetConfig } from '../configurations/types';

// Keyed by section `id` — note it's `buySideContacts` even when the
// listing/lease record key is `buyerSideContacts`.
const CONTACT_SECTION_IDS: readonly string[] = [
  'buySideContacts',
  'sellerSideContacts',
  'additionalContacts',
];

const isLeadSourceCard = (cardId: string) => cardId.endsWith('.leadSource');

/**
 * Drops matching fields from a card and from its layout, discarding any row
 * left empty so the remaining rows keep their intended widths.
 */
const removeFieldsFromCard = (
  card: Card,
  isExcluded: (fieldName: string) => boolean,
): Card => ({
  ...card,
  fields: card.fields.filter((field) => !isExcluded(field.name)),
  layout: card.layout
    ?.map((row) => row?.filter((item) => !isExcluded(item?.name ?? '')) ?? row)
    .filter((row) => !row || row.length > 0),
});

export type CoverSheetConfigData = {
  coverSheetConfig: CoverSheetConfig | null;
};

type UseCoverSheetConfigResult = {
  data: CoverSheetConfigData | undefined;
  loading: boolean;
};

type UseCoverSheetConfigOptions = {
  interactionId: string;
  interactionType: GQLInteractionType;
  representationType?: LeaseRepresentation | null;
  skip?: boolean;
};

/**
 * Hook that fetches the property address and builds the local cover sheet configuration
 * based on the property's state and interaction details.
 */
export function useCoverSheetConfig({
  interactionId,
  interactionType,
  representationType,
  skip,
}: UseCoverSheetConfigOptions): UseCoverSheetConfigResult {
  const { isLOMOfferExperience } = useOfferExperience({
    id: interactionId,
    type: interactionType,
  });

  const { txmCoverSheetContactsOffConfig } = useAppFlags();

  const { flags, loading: complianceFlagLoading } = useInteractionFlags(
    skip ? null : interactionId,
  );
  const txmMoveComplianceDataUpstream = flags?.txmMoveComplianceDataUpstream;

  const { data: interaction, isLoading: addressLoading } = useInteraction(
    interactionId,
    interactionType,
    skip,
  );

  const state = interaction?.property?.state;

  const coverSheetData = useMemo(() => {
    if (!state) {
      // This should only ever be empty if we're loading the address or there's an error
      return undefined;
    }

    if (complianceFlagLoading) {
      return undefined;
    }

    const config = getLocalCoverSheetConfig(
      state,
      interactionType,
      representationType,
    );

    let filteredConfig = config;

    // Compliance datapoints are captured upstream only when the flag is on
    if (!txmMoveComplianceDataUpstream) {
      const isComplianceField = (name: string) =>
        COMPLIANCE_UPSTREAM_FIELD_NAMES.includes(name);
      filteredConfig = {
        ...filteredConfig,
        sections: filteredConfig.sections.map((section) => ({
          ...section,
          cards: section.cards.map((card) =>
            removeFieldsFromCard(card, isComplianceField),
          ),
        })),
      };
    }

    // For LOM listings, the commission details card shows only Sell Side
    // (remove Buy Side = counterpartyCommission*)
    if (
      interactionType === GQLInteractionType.listing &&
      isLOMOfferExperience
    ) {
      const BUY_SIDE_FIELD_NAMES = [
        'counterpartyCommissionType',
        'counterpartyCommissionAmountPercent',
        'counterpartyCommissionAmountDollar',
        'counterpartyCommissionAdditionalAmount',
      ] as const;
      const isBuySideField = (name: string) =>
        BUY_SIDE_FIELD_NAMES.includes(
          name as (typeof BUY_SIDE_FIELD_NAMES)[number],
        );

      filteredConfig = {
        ...filteredConfig,
        sections: filteredConfig.sections.map((section) => {
          if (section.id !== 'propertyInfo') {
            return section;
          }
          return {
            ...section,
            cards: section.cards.map((card) => {
              if (card.id !== 'propertyInfo.commissionDetails') {
                return card;
              }
              return removeFieldsFromCard(card, isBuySideField);
            }),
          };
        }),
      };
    }

    if (txmCoverSheetContactsOffConfig) {
      // Lead Source lives inside a contact section but isn't a contact — pull
      // it out before dropping the sections and re-home it into Property Info.
      const leadSourceCards = filteredConfig.sections
        .filter((section) => CONTACT_SECTION_IDS.includes(section.id))
        .flatMap((section) =>
          section.cards.filter((card) => isLeadSourceCard(card.id)),
        );

      filteredConfig = {
        ...filteredConfig,
        sections: filteredConfig.sections
          .filter((section) => !CONTACT_SECTION_IDS.includes(section.id))
          .map((section) =>
            section.id === 'propertyInfo' && leadSourceCards.length
              ? { ...section, cards: [...section.cards, ...leadSourceCards] }
              : section,
          ),
      };
    }

    return {
      coverSheetConfig: filteredConfig,
    };
  }, [
    state,
    interactionType,
    representationType,
    isLOMOfferExperience,
    txmCoverSheetContactsOffConfig,
    txmMoveComplianceDataUpstream,
    complianceFlagLoading,
  ]);

  return {
    data: coverSheetData,
    loading: addressLoading || complianceFlagLoading,
  };
}
